import axios from 'axios';
import { apiClient } from "./api-client";
import { toProfileFields } from "@/lib/profile-fields";
import {
  forgetRefreshSession,
  markRefreshSession,
  SESSION_MODE_HEADERS,
  tokenExpiryMs,
} from "./session-refresh";

import {
  AuthSession,
  AuthUser,
  LoginPayload,
  RegisterPayload,
  ValidationGate,
} from '@/types/auth.types';

// Use relative path by default so Vite dev proxy can forward to backend and avoid CORS
// In production or when explicitly set, VITE_API_URL can point to the full API origin
// Note: apiClient handles the base URL and JWT interceptor for real API calls.
// Raw axios is kept for mock mode and endpoints that need withCredentials.

// Mode mock pour développement sans backend
// Définissez VITE_USE_MOCK=true dans votre .env pour activer le mode mock.
// Par défaut le mock est désactivé pour permettre l'utilisation du backend.
const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true';

// Type pour les utilisateurs mock
interface MockUser extends AuthUser {
  password: string;
}

// Utilisateurs mock
const MOCK_USERS: MockUser[] = [
  {
    username: 'demo_user',
    password: 'password123',
    id: '1',
    email: 'demo@yodi.com',
    role: 'user',
    isActive: true,
    contributionsCount: 42,
  },
  {
    username: 'admin',
    password: 'admin123',
    id: '2',
    email: 'admin@yodi.com',
    role: 'admin',
    isActive: true,
    contributionsCount: 150,
  },
  {
    username: 'moderator',
    password: 'mod123',
    id: '3',
    email: 'mod@yodi.com',
    role: 'moderator',
    isActive: true,
    contributionsCount: 89,
  },
  {
    username: 'collector',
    password: 'collector123',
    id: '4',
    email: 'collector@yodi.com',
    role: 'collector',
    isActive: true,
    contributionsCount: 21,
    name: 'Collecteur Demo',
  },
];

type RegisterApiResponse =
  | AuthSession
  | AuthUser
  | { user?: AuthUser; data?: AuthUser; profile?: AuthUser; token?: string };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isAuthUser = (value: unknown): value is AuthUser =>
  isRecord(value) && typeof value.id === 'string' && typeof value.username === 'string';

const toValidationGateOrUndefined = (value: unknown): ValidationGate | undefined => {
  if (!isRecord(value)) return undefined;
  if (typeof value.isBlocked !== 'boolean' || typeof value.contributionsSinceLastValidation !== 'number') {
    return undefined;
  }

  return {
    isBlocked: value.isBlocked,
    contributionsSinceLastValidation: value.contributionsSinceLastValidation,
    blockThreshold: typeof value.blockThreshold === 'number' ? value.blockThreshold : 50,
    warningThreshold: typeof value.warningThreshold === 'number' ? value.warningThreshold : 40,
    validationsSinceBlock: typeof value.validationsSinceBlock === 'number' ? value.validationsSinceBlock : 0,
    validationsRequired: typeof value.validationsRequired === 'number' ? value.validationsRequired : 100,
    blockedAt: typeof value.blockedAt === 'string' ? value.blockedAt : null,
    exempt: typeof value.exempt === 'boolean' ? value.exempt : undefined,
  };
};

const extractToken = (record: Record<string, unknown>): string | undefined => {
  const tokenLike =
    record.token ??
    record.access_token ??
    record.accessToken ??
    record.jwt ??
    (isRecord(record.data) ? record.data.token : undefined);

  return typeof tokenLike === 'string' ? tokenLike : undefined;
};

const decodeJWT = (token: string): Record<string, unknown> | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const decoded = JSON.parse(atob(parts[1]));
    return decoded;
  } catch {
    return null;
  }
};

export const getTokenPayload = (token: string): Record<string, unknown> | null => {
  return decodeJWT(token);
};

export const isTokenExpired = (token: string): boolean => {
  const expiry = tokenExpiryMs(token);
  return expiry === null || expiry < Date.now();
};

const normalizeSession = (payload: unknown): AuthSession => {
  if (isRecord(payload)) {
    // Cas 1: user objet complet avec id
    if (isAuthUser(payload.user)) {
      return { user: payload.user, token: extractToken(payload) };
    }

    // Cas 2: user objet depuis le backend avec role directement
    if (isRecord(payload.user) && typeof payload.user.username === 'string') {
      const token = extractToken(payload);
      const jwtPayload = token ? decodeJWT(token) : null;
      
      const rawRole = typeof payload.user.role === 'string'
        ? payload.user.role
        : (typeof jwtPayload?.role === 'string' ? jwtPayload.role : 'user');
      const normalizedRole = rawRole === 'super_admin' || rawRole === 'SUPER_ADMIN'
        ? 'super_admin'
        : rawRole === 'ADMIN'
        ? 'admin'
        : rawRole === 'collecteur' || rawRole === 'COLLECTOR' || rawRole === 'COLLECTEUR' || rawRole === 'ANNOTATOR' || rawRole === 'ANNOTATEUR'
        ? 'collector'
        : rawRole === 'MODERATOR'
        ? 'moderator'
        : rawRole === 'COMPTABLE' || rawRole === 'ACCOUNTANT'
        ? 'accountant'
        : rawRole === 'USER'
        ? 'user'
        : rawRole;

      const user: AuthUser = {
        id: String(payload.user.id ?? jwtPayload?.sub ?? Date.now()),
        username: payload.user.username,
        email: typeof payload.user.email === 'string' ? payload.user.email : undefined,
        name: typeof payload.user.name === 'string' ? payload.user.name : undefined,
        role: normalizedRole as any,
        isActive: typeof payload.user.isActive === 'boolean' ? payload.user.isActive : true,
        contributionsCount: typeof payload.user.contributionsCount === 'number' ? payload.user.contributionsCount : 0,
        createdAt: typeof payload.user.createdAt === 'string' ? payload.user.createdAt : undefined,
        // Copy additional user fields from server response
        country: typeof payload.user.country === 'string' ? payload.user.country : undefined,
        gender: typeof payload.user.gender === 'string' ? payload.user.gender : undefined,
        phoneNumber: typeof payload.user.phoneNumber === 'string' ? payload.user.phoneNumber : undefined,
        age: typeof payload.user.age === 'number' ? payload.user.age : undefined,
        isAcceptedTerms: typeof payload.user.isAcceptedTerms === 'boolean' ? payload.user.isAcceptedTerms : undefined,
        spokenLanguages: Array.isArray(payload.user.spokenLanguages) ? payload.user.spokenLanguages : undefined,
        contributionLanguages: Array.isArray(payload.user.contributionLanguages) ? payload.user.contributionLanguages : undefined,
        moderationLanguages: Array.isArray(payload.user.moderationLanguages) ? payload.user.moderationLanguages : undefined,
        preferredLocale: payload.user.preferredLocale === 'en' ? 'en' : 'fr',
        requiresLanguageSetup: typeof payload.user.requiresLanguageSetup === 'boolean' ? payload.user.requiresLanguageSetup : undefined,
        missingProfileFields: toProfileFields(payload.user.missingProfileFields),
        requiresProfileCompletion: typeof payload.user.requiresProfileCompletion === 'boolean' ? payload.user.requiresProfileCompletion : undefined,
        acceptedCount: typeof payload.user.acceptedCount === 'number' ? payload.user.acceptedCount : undefined,
        rejectedCount: typeof payload.user.rejectedCount === 'number' ? payload.user.rejectedCount : undefined,
        wantsToBePaid: typeof payload.user.wantsToBePaid === 'boolean' ? payload.user.wantsToBePaid : undefined,
        isPaidValidated: typeof payload.user.isPaidValidated === 'boolean' ? payload.user.isPaidValidated : undefined,
        validatedAt: typeof payload.user.validatedAt === 'string' ? payload.user.validatedAt : undefined,
        AudioContributionCount: typeof payload.user.AudioContributionCount === 'number' ? payload.user.AudioContributionCount : undefined,
        validatedAudioContributionCount: typeof payload.user.validatedAudioContributionCount === 'number' ? payload.user.validatedAudioContributionCount : undefined,
        confirmedContributionCount: typeof payload.user.confirmedContributionCount === 'number' ? payload.user.confirmedContributionCount : undefined,
        mustChangePassword: typeof payload.user.mustChangePassword === 'boolean' ? payload.user.mustChangePassword : undefined,
        validationGate: toValidationGateOrUndefined(payload.user.validationGate),
        lastPayoutDate: typeof payload.user.lastPayoutDate === 'string' ? payload.user.lastPayoutDate : '',
      };
      return { user, token };
    }

    if (isAuthUser(payload.data)) {
      return { user: payload.data, token: extractToken(payload) };
    }

    if (isAuthUser(payload.profile)) {
      return { user: payload.profile, token: extractToken(payload) };
    }

    if (isAuthUser(payload)) {
      return { user: payload, token: extractToken(payload) };
    }
  }

  console.error('Unexpected authentication response shape:', JSON.stringify(payload, null, 2));
  throw new Error('Unexpected authentication response shape');
};

const persistSession = (session: AuthSession) => {
  const { user, token } = session;
  forgetRefreshSession();
  localStorage.setItem('yodi-user', JSON.stringify(user));
  if (token) {
    localStorage.setItem('yodi-token', token);
  }
};

export interface ForgotPasswordPayload {
  identifier: string;
}

export type PasswordResetMethod = 'email' | 'sms' | 'unknown';

export interface ForgotPasswordResponse {
  message: string;
  method: PasswordResetMethod;
  maskedTarget: string | null;
  expiresInSeconds: number | null;
}

export interface VerifyPasswordResetOtpPayload {
  identifier: string;
  otp: string;
}

export interface VerifyPasswordResetOtpResponse {
  valid: boolean;
  otpToken: string;
  expiresInSeconds: number;
}

export interface ResetPasswordPayload {
  password: string;
  confirmPassword: string;
}

export interface ResetPasswordWithOtpPayload extends ResetPasswordPayload {
  otpToken: string;
}

export interface AuthResponse {
  accessToken: string;
  user?: {
    id: string;
    username: string;
    email?: string;
    role: string;
    name: string;
  };
}

let currentUserCache: {
  token: string;
  user: AuthResponse['user'];
  expiresAt: number;
} | null = null;
let currentUserInflight: {
  token: string;
  promise: Promise<AuthResponse['user']>;
} | null = null;

const CURRENT_USER_TTL_MS = 30_000;
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function fetchCurrentUserWithRetry(): Promise<AuthResponse['user']> {
  let attempt = 0;
  for (;;) {
    try {
      const { data } = await apiClient.get('/auth/me');
      return data;
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 429 && attempt < 2) {
        attempt += 1;
        await sleep(attempt * 750);
        continue;
      }
      throw err;
    }
  }
}

export const authApi = {
  register: async (data: RegisterPayload): Promise<AuthSession> => {
    if (USE_MOCK) {
      // Simulation d'un délai réseau
      await new Promise((resolve) => setTimeout(resolve, 500));

      const newUser: AuthUser = {
        id: String(Date.now()),
        username: data.username,
        email: data.email,
        role: 'user',
        isActive: true,
        contributionsCount: 0,
        createdAt: new Date().toISOString(),
        name: data.name,
        phoneNumber:data.phoneNumber,
      };
      

      const session: AuthSession = {
        user: newUser,
        token: `mock_token_${newUser.id}`,
      };

      persistSession(session);
      return session;
    }

    const response = await apiClient.post<RegisterApiResponse>(
      '/auth/register',
      data,
    );

    const session = normalizeSession(response.data);
    persistSession(session);
    return session;
  },

  login: async (data: LoginPayload): Promise<AuthSession> => {
    if (USE_MOCK) {
      await new Promise((resolve) => setTimeout(resolve, 500));

      const user = MOCK_USERS.find(
        (mockUser) =>
          mockUser.username === data.username && mockUser.password === data.password,
      );

      if (!user) {
        throw new Error("Nom d'utilisateur ou mot de passe incorrect");
      }

      const { password, ...userWithoutPassword } = user;
      const session: AuthSession = {
        user: userWithoutPassword,
        token: `mock_token_${user.id}`,
      };

      persistSession(session);
      return session;
    }

    // Spec 008: the web client opts in to short tokens renewed by an httpOnly cookie.
    const response = await apiClient.post<RegisterApiResponse & { isFirstLogin?: unknown }>(
      '/auth/login',
      data,
      { withCredentials: true, headers: SESSION_MODE_HEADERS },
    );

    const session = normalizeSession(response.data);
    persistSession(session);
    markRefreshSession();
    // Older backends do not send isFirstLogin: the session then has none.
    const isFirstLogin = response.data?.isFirstLogin;
    return typeof isFirstLogin === 'boolean' ? { ...session, isFirstLogin } : session;
  },
  async getCurrentUser(): Promise<AuthResponse['user']> {
  const token = localStorage.getItem('yodi-token') ?? '';
  const now = Date.now();
  if (
    currentUserCache &&
    currentUserCache.token === token &&
    currentUserCache.expiresAt > now
  ) {
    return currentUserCache.user;
  }
  if (currentUserInflight && currentUserInflight.token === token) {
    return currentUserInflight.promise;
  }
  const promise = fetchCurrentUserWithRetry();
  currentUserInflight = { token, promise };
  try {
    const user = await promise;
    currentUserCache = { token, user, expiresAt: now + CURRENT_USER_TTL_MS };
    return user;
  } finally {
    currentUserInflight = null;
  }
},
  async forgotPassword(payload: ForgotPasswordPayload): Promise<ForgotPasswordResponse> {
    const { data } = await apiClient.post<ForgotPasswordResponse>(
      '/users/forgot-password',
      payload
    );
    return data;
  },

  initiateGoogleLogin(): void {
    const baseUrl = import.meta.env.VITE_API_URL || '/v1';
    window.location.href = `${baseUrl}/auth/google`;
  },

  /**
   * Traite le callback Google après authentification
   * Appelé depuis la page /auth/google/callback
   */
  async handleGoogleCallback(): Promise<AuthResponse> {
    const baseUrl = import.meta.env.VITE_API_URL || '/v1';
    const { data } = await axios.get(`${baseUrl}/auth/me`, {
      withCredentials: true,
    });

    return {
      accessToken: data.accessToken,
      user: data.user,
    };
  },

  /**
   * Réinitialisation du mot de passe avec le token reçu par email
   */
  async resetPassword(
    token: string,
    payload: ResetPasswordPayload
  ): Promise<{ message: string }> {
    const { data } = await apiClient.post<{ message: string }>(
      `/users/reset-password/${token}`,
      payload
    );
    return data;
  },

  /**
   * Vérifier la validité d'un token de réinitialisation
   */
  async verifyResetToken(token: string): Promise<{ valid: boolean; email?: string }> {
    try {
      const { data } = await apiClient.get(
        `/users/verify-reset-token/${token}`
      );
      return data;
    } catch {
      return { valid: false };
    }
  },

  async verifyPasswordResetOtp(
    payload: VerifyPasswordResetOtpPayload,
  ): Promise<VerifyPasswordResetOtpResponse> {
    const { data } = await apiClient.post<VerifyPasswordResetOtpResponse>(
      '/users/verify-reset-otp',
      payload,
    );
    return data;
  },

  async resetPasswordWithOtp(
    payload: ResetPasswordWithOtpPayload,
  ): Promise<{ message: string }> {
    const { data } = await apiClient.post<{ message: string }>(
      '/users/reset-password/otp',
      payload,
    );
    return data;
  },

  async forceChangePassword(payload: {
    password: string;
    preferredLocale: 'fr' | 'en';
    spokenLanguages: string[];
    contributionLanguages: string[];
    moderationLanguages: string[];
  }): Promise<{ message: string; user: AuthUser }> {
    const { data } = await apiClient.post<{ message: string; user: AuthUser }>(
      '/users/force-change-password',
      payload,
    );
    return data;
  },
};



